/**
 * The currency module for REACT COMPONENTS. Each hook subscribes to the merchant's money settings,
 * so the component re-renders when the currency or format changes, and only then.
 *
 * Outside a component (a helper, a saga, an event handler) use the plain twins in `./shared`
 * instead: `money()`, `amount()`, `currencySymbol()`, `moneySettings()`. They read the store once,
 * at the moment they are called.
 *
 * A `packages/ui` leaf that must stay store-free takes a `money: MoneyFormat` prop instead, and the
 * screen above it passes `useMoney()`. The kiosk's leaves use `useKioskMoney()` for the same reason.
 */
import { useCallback } from "react";
import { useSelector } from "react-redux";

import { formatAmount, formatMoney } from "./format";
import type { MoneyFormat, MoneyOptions, MoneySettings } from "./format";
import { selectMoneySettings } from "./selectors";
import type { StateWithBusiness } from "./selectors";

/**
 * This merchant's `MoneySettings`, kept current. For a host that must hand the format across a
 * boundary the store cannot cross: a provider, a second window, a pure child.
 *
 * @example const settings = useMoneySettings(); // { symbol: "₹", decimals: 2, commaStyle: "lakhs" }
 */
export function useMoneySettings(): MoneySettings {
  return useSelector(selectMoneySettings);
}

/**
 * The currency symbol alone, kept current: for a label or an input prefix. Re-renders only when
 * the SYMBOL changes, not when the precision or comma style does.
 *
 * @example const symbol = useCurrencySymbol(); // "₹"   →   <label>Amount ({symbol})</label>
 */
export function useCurrencySymbol(): string {
  return useSelector((state: StateWithBusiness) => selectMoneySettings(state).symbol);
}

/**
 * 🔴 **The everyday hook.** A function that writes a complete money string for this merchant.
 *
 * Its identity changes only when the money settings do, so it is safe in a `useMemo` or
 * `useCallback` dependency list (and listing it is what rebuilds a memo when the currency changes)
 * and safe to pass to a memoised child.
 *
 * @example
 * const money = useMoney();
 * money(1234.5)                   // "₹1,234.50"
 * money(100, { decimals: 0 })     // "₹100"
 */
export function useMoney(): MoneyFormat {
  const settings = useMoneySettings();
  return useCallback<MoneyFormat>(
    (value, options) => formatMoney(value, settings, options),
    [settings],
  );
}

/**
 * `useMoney()` without the symbol: the digits only. For a screen that draws the symbol itself in
 * its own styled node. Same options, same identity rule.
 *
 * @example
 * const amount = useAmount();
 * amount(1234.5)                  // "1,234.50"
 */
export function useAmount(): (value: unknown, options?: MoneyOptions) => string {
  const settings = useMoneySettings();
  return useCallback(
    (value: unknown, options?: MoneyOptions) => formatAmount(value, settings, options),
    [settings],
  );
}
